import { useMemo } from 'react';
import classNames from 'classnames';
import { isArray } from 'lodash-es';
import { playAudio, parsingAI } from '@/utils/audioPlay';
import { IGenericObj, IObjNumber, IPictureCompletionProp, IlistItem } from '@/utils/ctype';
import i18n from '@/locales/init'; 
import Input from '../../../components/MyInput';
import HtmlParser from '../../../components/HtmlParser';
import style from './index.module.less';

const getGableMergeInfo = (content: Array<Array<string | [number, number]>>): Array<Array<{ visible: boolean; col: number; row: number; }>> => {
  const mergeGroup: Array<Array<{ visible: boolean; col: number; row: number; }>> = [];

  for (let i = 0; i < content.length; ++i) {
    if (!mergeGroup[i]) {
      mergeGroup[i] = [];
    }

    for (let j = 0; j < content[i].length; ++j) {
      if (typeof content[i][j] === 'string') {
        mergeGroup[i][j] = { visible: true, col: 1, row: 1, };
      } else if (isArray(content[i][j])) {
        const r = content[i][j][0];
        const c = content[i][j][1];
        if (typeof r === 'number' && typeof c === 'number') {
          mergeGroup[i][j] = { visible: false, col: 0, row: 0, };
          if (i === r) {
            mergeGroup[r][c].col = j - c + 1;
          } else {
            mergeGroup[r][c].row = i - r + 1;
          }
        }
      }
    }
  }

  return mergeGroup;
};

function groupAdjacentByLength(arr: string[][]): {
  tableData: string[][][] | [];
  mergeInfo: Array<Array<Array<{ visible: boolean; col: number; row: number; }>>>;
  mapIndextoRealIndex: IObjNumber;
} {
  
  if (arr.length === 0) {
    return {tableData: [], mergeInfo: [], mapIndextoRealIndex: {}};
  }

  const grouped = [];
  let currentGroup = [arr[0]];
  let arrIndex = 0;
  const mapIndextoRealIndex = {[`${arrIndex}_0`]: 0};

  for (let i = 1; i < arr.length; i++) {
    if (arr[i].length === arr[i - 1].length) {
      currentGroup.push(arr[i]);
    } else {
      arrIndex++;
      grouped.push(currentGroup);
      currentGroup = [arr[i]];
    }
    mapIndextoRealIndex[`${arrIndex}_${currentGroup.length - 1}`] = i;
  }

  // console.log('currentGroup', currentGroup);
  // 添加最后一个组
  grouped.push(currentGroup);

  return {tableData: grouped, mergeInfo: grouped.map(getGableMergeInfo), mapIndextoRealIndex};
}



export default function Table(props: IPictureCompletionProp & {inputClass?: string;}) {
  const { data, saveAnswerHandle, groupId, type, answerMap, isAI, isListen, keyIndex, answerAllText, inputClass, setFocusIndexHandle, feedbackMap} = props;
  const { list = [], table } = data;
  const {listMap, listItem, listIdToIndexMap} = useMemo(() =>{
    const listMap: IObjNumber = {};
    const listItem: IGenericObj<IlistItem> = {};
    const listIdToIndexMap: IObjNumber = {};
    list.forEach((item, index: number) => {
      listMap[item.id] = item.number;
      listItem[item.id] = item;
      listIdToIndexMap[item.id] = index;
    });
    return {listMap, listItem, listIdToIndexMap};
  }, [list]);
  

  const {tableData: TableData, mapIndextoRealIndex, mergeInfo } = groupAdjacentByLength(table.content);
  // console.log('mapIndextoRealIndex', mapIndextoRealIndex);

  // const hasTitle = TableData.length === 1;

  const handleInput = (event: React.FormEvent<HTMLInputElement>, id: string) => {
    const value = (event.target as HTMLInputElement).value;
    saveAnswerHandle(listMap[id], {
      group_id: groupId,
      id: Number(id),
      type,
      answer: [value]
    });
  };

  const createAIType = (answer: string, listItem: IlistItem) => {
    if (!isAI) {
      return;
    }
    if (feedbackMap && listItem.id) {
      return feedbackMap[listItem.id];
    }
  };

  const TextWithInput = (text: string) => {
    // 使用正则表达式匹配 $数字$ 的形式
    const regex = /\$(\d+)\$/g;
    // 将字符串分割成数组，并保留分割点
    const parts = [];
    let lastIndex = 0;
    let match;
  
    while ((match = regex.exec(text)) !== null) {
      // 添加非匹配部分
      parts.push(<HtmlParser htmlString={text.slice(lastIndex, match.index)} key={`t${match.index}`}></HtmlParser>);

      const id = match[1];
      const number = listMap[match[1]];
      const item = listItem[id];
      const itemIndex = listIdToIndexMap[id];
      const value=answerMap[`${groupId}_${type}_${id}`] && answerMap[`${groupId}_${type}_${id}`][0];

      if (item) {

        // 添加匹配部分的数字，包裹在 <span> 中
        parts.push(<Input 
          className={classNames(inputClass, `question${number}`)} 
          type={createAIType(value, item)}
          answerKey={`answerAllText.${number - 1}`}
          answer={answerAllText[number - 1]}
          dataKey={`question.question.${keyIndex}.list.${itemIndex}.display_answerHtml`} 
          displayAnswer={item?.display_answerHtml} 
          numberKey={`$question.question.${keyIndex}.list.${itemIndex}.number`}
          numberHtml={item.numberHtml}
          value={value} 
          placeholder={String(number)} 
          key={match.index} 
          id={`question${number}`} 
          onInput={(event) => {handleInput(event, id);}} 
          playAudio={() => {playAudio(item);}} 
          parsingAI={() => {parsingAI(item);}} isListen={isListen}
          onFocus={()=>{setFocusIndexHandle&&setFocusIndexHandle(number);}}
        ></Input>);
      } else {
        parts.push(<HtmlParser htmlString={match[0]} key={`t${match.index}`}></HtmlParser>);
      }
      // 更新 lastIndex
      lastIndex = regex.lastIndex;
    }
    // 添加剩余的非匹配部分
    parts.push(<HtmlParser htmlString={text.slice(lastIndex)} key={'last'}></HtmlParser>);
  
    return parts;
  };

  return (
    <div className={style.tableQuestion}>
      {
        !!data.title ? data.title.split('\n').map((s: string, index: number) => {
          if (index === 0) {
            return <div key={index} className={style.title} data-key={`question.question.${keyIndex}.table.title`}><HtmlParser htmlString={s}></HtmlParser></div>;
          }
          return <div key={index} className={style.info} data-key={`question.question.${keyIndex}.table.info`}><HtmlParser htmlString={s}></HtmlParser></div>;

        }) : null
      }
      {
        TableData.map((itemTable, index3) => {
          return (
            <table className={classNames(style.table, itemTable.length === 1&& itemTable[0].length === 1 ? style.titleTable: '')} key={index3} >
              {(itemTable || []).map((item1, index1: number) => {
                return (
                  <tr key={index1}>
                    {
                      item1.map((item2, index2: number) => {
                        const m = mergeInfo[index3][index1][index2];
                        if (m.visible) {
                          return <td colSpan={m.col} rowSpan={m.row} key={index2} className={style.tableCell} data-key={`question.question.${keyIndex}.table.content.${mapIndextoRealIndex[`${index3}_${index1}`]}.${index2}`}>
                            {
                              item2.split('\n').map((s, index4) => {
                                return <div className={style.tableCellLine} key={`t.table${index3}.row${index1}.col${index2}.line${index4}`}>{TextWithInput(s)}</div>;
                              })
                            }
                          </td>;
                        } else {
                          return null;
                        }
                      })
                    }
                  </tr>
                );
              })}
            </table>
          );
        })
      }
    </div>
    
  );
}